import { createContext, useState, useContext, useEffect } from 'react';
import { cartAPI } from '../services/api';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartCount, setCartCount] = useState(0);
  const [cartItems, setCartItems] = useState([]);
  const [cartData, setCartData] = useState({}); // Store full backend response like mobile app
  const [isLoading, setIsLoading] = useState(false);

  // Fetch cart count and items (matching mobile app pattern)
  const fetchCart = async () => {
    try {
      setIsLoading(true);
      const response = await cartAPI.getCart();

      if (response && response.data) {
        // Store full backend response like mobile app
        setCartData(response.data || {});
        
        const items = response.data.items || response.data.cart?.items || [];
        const count = response.data.count;


        setCartItems(items);

        // Use backend's count if available, otherwise calculate from items
        if (count !== undefined) {
          setCartCount(count);
        } else {
          const totalCount = items.reduce((total, item) => total + (item.quantity || 1), 0);
          setCartCount(totalCount);
        }

      } else {
        setCartItems([]);
        setCartCount(0);
        setCartData({});
      }
    } catch (error) {

      // Check for CORS error
      if (error.message?.includes('Network error') || error.message?.includes('Failed to fetch')) {
        // Don't reset cart on CORS error - keep existing state
        // User can still use the app, just can't fetch cart from backend
      } else if (error.message?.includes('401') || error.message?.includes('Invalid or expired token') || error.message?.includes('Authorization token required')) {
        // If user is not authenticated, reset cart silently
        setCartCount(0);
        setCartItems([]);
        setCartData({});
        // Don't show any alerts here - the api.js handleError already handles it
      } else {
        // For other errors, just log and keep existing state
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Add item to cart (matching mobile app pattern)
  const addToCart = async (productId, quantity = 1) => {
    try {
      const response = await cartAPI.addItem(productId, quantity);

      // Refresh cart after adding
      await fetchCart();

      return response;
    } catch (error) {
      throw error;
    }
  };

  // Update item quantity in cart
  const updateCartItem = async (itemId, quantity) => {
    try {
      await cartAPI.updateItem(itemId, quantity);

      // Refresh cart after updating
      await fetchCart();
    } catch (error) {
      throw error;
    }
  };

  // Remove item from cart
  const removeFromCart = async (itemId) => {
    try {
      await cartAPI.removeItem(itemId);

      // Refresh cart after removing
      await fetchCart();
    } catch (error) {
      throw error;
    }
  };

  // Clear cart
  const clearCart = async () => {
    try {
      await cartAPI.clear();

      setCartCount(0);
      setCartItems([]);
    } catch (error) {
      throw error;
    }
  };

  // Refresh cart count (useful after navigation)
  const refreshCart = () => {
    fetchCart();
  };

  // Fetch cart on mount (only if user is authenticated)
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (token) {
      fetchCart();
    } else {
      setIsLoading(false);
    }
  }, []);

  const value = {
    cartCount,
    cartItems,
    cartData, // Expose full cart data like mobile app
    isLoading,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    refreshCart,
    fetchCart,
    // Expose individual cart operations for direct use
    addItem: addToCart,
    updateItem: updateCartItem,
    removeItem: removeFromCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};

export default CartContext;
